import React, { useState, useMemo } from "react";
import ResourceInfo from "./resourceInfo";
import { format, isValid, parseISO } from "date-fns";

const ResourceSchedule = ({
  resource,
  currentDate,
  showWorkingHours,
  dailyNote,
  handleNoteUpdate,
  handleNoteDelete,
  onTimeHover,
  onTimeClick,
  onTimeContextMenu,
  events = [],
}) => {
  const [hoveredRow, setHoveredRow] = useState(null);

  // Ensure currentDate is a valid Date object
  const validDate = useMemo(() => {
    if (!currentDate || !isValid(currentDate)) {
      return new Date();
    }
    return currentDate;
  }, [currentDate]);

  // Define the time range to display (24 hours, with 15 minute intervals)
  const timeRows = useMemo(() => {
    return Array.from({ length: 96 }).map((_, index) => {
      const hour = Math.floor(index / 4);
      const minute = (index % 4) * 15;
      const formattedHour = hour.toString().padStart(2, "0");
      const formattedMinute = minute.toString().padStart(2, "0");
      return `${formattedHour}:${formattedMinute}`;
    });
  }, []);

  // Function to check if a time slot is available (default to true for all slots)
  const isTimeSlotAvailable = () => {
    return true;
  };

  // Current time for highlighting the current time row
  const currentTime = useMemo(() => {
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinute = now.getMinutes();
    const roundedMinute = Math.floor(currentMinute / 15) * 15;
    return `${currentHour.toString().padStart(2, "0")}:${roundedMinute.toString().padStart(2, "0")}`;
  }, []);

  // Process events for display
  const processedEvents = useMemo(() => {
    return events.map(event => {
      // Check if the event uses start/end or startTime/endTime fields
      const startTimeField = event.startTime || event.start;
      const endTimeField = event.endTime || event.end;
      
      // Ensure start and end times are valid dates
      const startTime = startTimeField ? parseISO(startTimeField) : null;
      const endTime = endTimeField ? parseISO(endTimeField) : null;
      
      if (!startTime || !endTime) {
        console.warn('Invalid start or end time for event:', event);
        return null;
      }
      
      // Calculate the start and end indices in the timeRows array
      const startHour = startTime.getHours();
      const startMinute = startTime.getMinutes();
      const startIndex = (startHour * 4) + Math.floor(startMinute / 15);
      
      const endHour = endTime.getHours();
      const endMinute = endTime.getMinutes();
      const endIndex = (endHour * 4) + Math.ceil(endMinute / 15);
      
      return {
        ...event,
        startIndex,
        endIndex,
        startTime,
        endTime,
        color: event.status === 'confirmed' ? 'bg-blue-200' : 'bg-yellow-200'
      };
    }).filter(Boolean);
  }, [events]);

  // Check if a time slot has an event
  const getEventForTimeSlot = (timeIndex) => {
    return processedEvents.find(event => 
      timeIndex >= event.startIndex && timeIndex < event.endIndex
    );
  };

  // Generate the time slot className based on various conditions
  const getTimeSlotClassName = (time, index) => {
    const isAvailable = isTimeSlotAvailable(time);
    const isHovered = hoveredRow === index;
    const isCurrentTime = time === currentTime;
    const event = getEventForTimeSlot(index);
    
    let className = "h-8 border-b border-gray-100 px-2 flex items-center text-xs transition-colors";
    
    if (event) {
      // This is a booked slot with an event
      className += ` ${event.color} cursor-pointer`;
      
      // If this is the first cell of the event, add top border
      if (index === event.startIndex) {
        className += " border-t border-l border-r border-gray-300 rounded-t";
      } 
      // If this is the last cell of the event, add bottom border
      else if (index === event.endIndex - 1) {
        className += " border-b border-l border-r border-gray-300 rounded-b";
      }
      // Middle cells of the event
      else {
        className += " border-l border-r border-gray-300";
      }
    } else {
      // This is an available slot
      className += isAvailable ? " bg-green-50" : " bg-gray-100";
      className += isHovered ? " bg-green-100 cursor-pointer" : "";
    }
    
    // Add current time indicator regardless of booking status
    if (isCurrentTime) {
      className += " border-red-500 border-t-2";
    }
    
    return className;
  };

  // Handle right-click on a time slot
  const handleContextMenu = (e, time) => {
    if (onTimeContextMenu) {
      onTimeContextMenu(e, time);
    }
  };

  return (
    <div className="overflow-y-auto max-h-[calc(100vh-180px)]">
      {timeRows.map((time, index) => {
        const event = getEventForTimeSlot(index);
        
        return (
          <div
            key={`time-slot-${index}`}
            className={getTimeSlotClassName(time, index)}
            onMouseEnter={() => {
              setHoveredRow(index);
              if (onTimeHover) onTimeHover(time);
            }}
            onMouseLeave={() => setHoveredRow(null)}
            onClick={() => {
              if (onTimeClick) onTimeClick(time);
            }}
            onContextMenu={(e) => handleContextMenu(e, time)}
          >
            {event ? (
              <div className="w-full truncate">
                {event.title || 'Booked'}
              </div>
            ) : (
              <div className="w-full">
                {index % 4 === 0 && !event && (
                  <span className="text-gray-400">{time}</span>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ResourceSchedule;
